import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  ShieldCheck,
  FileText,
  Sparkles,
  Clock,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Circle,
  ExternalLink,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';
import { cn } from '@/lib/utils';

type Tier = 'STARTER' | 'FULL';

interface Manifest {
  tier: Tier;
  artefactCount: number;
  artefacts: Array<{ id: string; title: string; filename: string }>;
  pricing: { amount: number; currency: string; cadence: string; sla: string };
  frameworks: string[];
}

interface ReadinessCheck {
  key: string;
  title: string;
  detail: string;
  ok: boolean;
  severity: 'required' | 'recommended';
  count?: number;
  minimum?: number;
  fixUrl?: string;
}

interface Readiness {
  ready: boolean;
  blocking: string[];
  checks: ReadinessCheck[];
  summary: { requiredOk: number; requiredTotal: number; recommendedOk: number; recommendedTotal: number };
}

interface SourceRecord {
  id: string;
  title: string;
  subtitle: string;
  editUrl: string;
}

interface SourceRecordsResp {
  modelCards: SourceRecord[];
  risks: SourceRecord[];
  ropaActivities: SourceRecord[];
  retentionPolicies: SourceRecord[];
}

export function CompliancePackPage() {
  const [tier, setTier] = useState<Tier>('FULL');
  const [exporting, setExporting] = useState(false);
  const tenant = useAuthStore((s) => s.tenant);

  const manifest = useQuery({
    queryKey: ['compliance-pack', 'manifest', tier],
    queryFn: async () => (await api.get<Manifest>(`/api/compliance-pack/manifest?tier=${tier}`)).data,
  });

  const readiness = useQuery({
    queryKey: ['compliance-pack', 'readiness'],
    queryFn: async () => (await api.get<Readiness>('/api/compliance-pack/readiness')).data,
  });

  const sourceRecords = useQuery({
    queryKey: ['compliance-pack', 'source-records'],
    queryFn: async () => (await api.get<SourceRecordsResp>('/api/compliance-pack/source-records')).data,
  });

  async function exportPack() {
    setExporting(true);
    try {
      const res = await api.post(`/api/compliance-pack/export?tier=${tier}`, {}, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] ?? `praxara-compliance-pack-${tier.toLowerCase()}.zip`;
      const blob = new Blob([res.data as Blob], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Compliance Pack downloaded', filename);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed';
      toast.error('Pack export failed', msg);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Compliance Pack</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Generate the EU AI Act, Annex 22, 21 CFR Part 11 and GDPR artefacts your QA team needs to clear procurement
            and audit before <strong className="text-foreground">2 August 2026</strong> enforcement. Every artefact is
            built from your live tenant data.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>Tenant: <strong className="text-foreground">{tenant?.name}</strong></span>
        </div>
      </div>

      {/* Tier selector */}
      <div className="grid gap-4 md:grid-cols-2">
        <TierCard
          selected={tier === 'STARTER'}
          onSelect={() => setTier('STARTER')}
          name="Starter"
          price="£5,000"
          cadence="one-time"
          tagline="Quick procurement bypass · single signing authority"
          features={[
            'Annex III classification',
            'AI Model Cards (Article 11)',
            'Risk Register seed (Article 9)',
            'PDF + Markdown sources',
            'Delivered within 5 business days',
          ]}
        />
        <TierCard
          selected={tier === 'FULL'}
          onSelect={() => setTier('FULL')}
          name="Full"
          price="£30,000"
          cadence="annual"
          featured
          tagline="Concierge onboarding · quarterly refresh · auditor-ready"
          features={[
            'Everything in Starter',
            'GDPR Article 30 ROPA',
            '21 CFR Part 11 compliance matrix',
            'Audit Chain Summary',
            'Retention Policy',
            'Quarterly refresh + Annex 22 updates',
          ]}
        />
      </div>

      {/* What's included */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            What's in the {tier === 'STARTER' ? 'Starter' : 'Full'} bundle
          </CardTitle>
          <CardDescription>
            Generated from live tenant data on demand. Every artefact arrives as a signing-ready PDF + a Markdown source for diff-able review.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {manifest.isLoading && <div className="text-sm text-muted-foreground">Loading manifest…</div>}
          {manifest.data && (
            <div className="grid gap-2 md:grid-cols-2">
              {manifest.data.artefacts.map((a, i) => (
                <div key={a.id} className="flex items-start gap-3 rounded-md border bg-muted/20 px-3 py-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-semibold">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{a.title}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{a.filename}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {manifest.data && (
            <div className="mt-4 flex flex-wrap gap-2">
              {manifest.data.frameworks.map((f) => (
                <Badge key={f} variant="secondary" className="text-[10px]">
                  <ShieldCheck className="mr-1 h-3 w-3" /> {f}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Readiness checklist -- block export until required checks pass. */}
      <ReadinessChecklist data={readiness.data} loading={readiness.isLoading} />

      {/* Source records that'll land in the next export */}
      <SourceRecordsCard data={sourceRecords.data} loading={sourceRecords.isLoading} />

      {/* Generate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Generate now</CardTitle>
          <CardDescription>
            Builds the bundle live from this tenant's model cards, risk register, ROPA, retention policies and audit
            chain. The export is recorded in the audit log automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button
            onClick={exportPack}
            disabled={exporting || (readiness.data ? !readiness.data.ready : false)}
            className="gap-2"
          >
            <Download className="h-4 w-4" /> {exporting ? 'Generating…' : `Export ${tier === 'STARTER' ? 'Starter' : 'Full'} Pack (ZIP)`}
          </Button>
          <Link to="/compliance-pack/shares" className="text-sm text-primary hover:underline">
            Share with an auditor →
          </Link>
          <p className="text-xs text-muted-foreground">
            Manager+ role required. Export is logged with action <code className="rounded bg-muted px-1">EXPORT_GENERATED</code> on the audit chain.
          </p>
          {readiness.data && !readiness.data.ready && (
            <p className="basis-full text-xs text-amber-700 dark:text-amber-400">
              Required readiness checks above must pass before you can export. Recommended checks are advisory.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Why-buy strip */}
      <div className="grid gap-3 md:grid-cols-3">
        <InfoTile
          icon={AlertTriangle}
          title="The deadline is real"
          body="EU AI Act high-risk obligations bind from 2 August 2026. Annex 22 (GxP-for-AI) finalises 2026. Pharma QA teams need this paperwork before enforcement, not after."
        />
        <InfoTile
          icon={Sparkles}
          title="Generated, not templated"
          body="Every figure, every risk, every model card comes from your live tenant data. No copy-paste. Re-run quarterly and the pack reflects what actually changed."
        />
        <InfoTile
          icon={FileText}
          title="One signing-day artefact"
          body="QA, DPO, AI Officer review the pack, sign, submit. Same workflow they use for any controlled document. No new tools, no new training."
        />
      </div>
    </div>
  );
}

function TierCard({
  selected,
  onSelect,
  name,
  price,
  cadence,
  tagline,
  features,
  featured,
}: {
  selected: boolean;
  onSelect: () => void;
  name: string;
  price: string;
  cadence: string;
  tagline: string;
  features: string[];
  featured?: boolean;
}) {
  return (
    <button
      onClick={onSelect}
      className={`group relative rounded-lg border p-5 text-left transition-all ${
        selected
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border bg-card hover:border-primary/40 hover:shadow-sm'
      }`}
    >
      {featured && (
        <div className="absolute -top-2 right-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary-foreground">
          Recommended
        </div>
      )}
      <div className="flex items-baseline gap-2">
        <div className="font-display text-lg">{name}</div>
        {selected && <CheckCircle2 className="h-4 w-4 text-primary" />}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{tagline}</div>
      <div className="mt-4 flex items-baseline gap-1">
        <div className="font-display text-3xl font-semibold">{price}</div>
        <div className="text-xs text-muted-foreground">/ {cadence}</div>
      </div>
      <ul className="mt-4 space-y-1.5 text-xs">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-muted-foreground">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
        {selected ? 'Selected' : 'Select'} <ArrowRight className="h-3 w-3" />
      </div>
    </button>
  );
}

function InfoTile({ icon: Icon, title, body }: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <Icon className="h-5 w-5 text-primary" />
      <div className="mt-2 text-sm font-medium">{title}</div>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

function ReadinessChecklist({ data, loading }: { data: Readiness | undefined; loading: boolean }) {
  if (loading || !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pre-export readiness</CardTitle>
          <CardDescription>Checking your tenant for missing data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  const required = data.checks.filter((c) => c.severity === 'required');
  const recommended = data.checks.filter((c) => c.severity === 'recommended');
  return (
    <Card className={cn(!data.ready && 'border-amber-300/60')}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Pre-export readiness</CardTitle>
            <CardDescription>
              {data.ready
                ? 'All required checks are green. Recommended checks are advisory.'
                : 'Some required data is missing. Fix the items below before exporting.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <Pill ok={data.summary.requiredOk === data.summary.requiredTotal}>
              Required {data.summary.requiredOk}/{data.summary.requiredTotal}
            </Pill>
            <Pill ok={data.summary.recommendedOk === data.summary.recommendedTotal} muted>
              Recommended {data.summary.recommendedOk}/{data.summary.recommendedTotal}
            </Pill>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {[...required, ...recommended].map((c) => (
          <CheckRow key={c.key} c={c} />
        ))}
      </CardContent>
    </Card>
  );
}

function CheckRow({ c }: { c: ReadinessCheck }) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 rounded-md border p-3',
        c.ok
          ? 'border-emerald-200/60 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/30'
          : c.severity === 'required'
          ? 'border-amber-300/70 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/30'
          : 'border-border bg-muted/30',
      )}
    >
      <div className="flex items-start gap-3">
        {c.ok ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        ) : (
          <Circle
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0',
              c.severity === 'required' ? 'text-amber-600' : 'text-muted-foreground',
            )}
          />
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {c.title}
            {!c.ok && (
              <Badge variant={c.severity === 'required' ? 'warning' : 'secondary'} className="text-[10px]">
                {c.severity}
              </Badge>
            )}
            {c.count != null && c.minimum != null && (
              <span className="text-[11px] text-muted-foreground">
                {c.count} / {c.minimum} required
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
        </div>
      </div>
      {!c.ok && c.fixUrl && (
        <Link
          to={c.fixUrl}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          Fix <ExternalLink className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

function SourceRecordsCard({ data, loading }: { data: SourceRecordsResp | undefined; loading: boolean }) {
  if (loading || !data) {
    return null;
  }
  const groups: Array<{ key: string; title: string; items: SourceRecord[] }> = [
    { key: 'modelCards', title: 'AI Model cards', items: data.modelCards },
    { key: 'risks', title: 'Risk register', items: data.risks },
    { key: 'ropaActivities', title: 'ROPA activities', items: data.ropaActivities },
    { key: 'retentionPolicies', title: 'Retention policies', items: data.retentionPolicies },
  ];
  const total = groups.reduce((acc, g) => acc + g.items.length, 0);
  if (total === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Source records ({total})</CardTitle>
        <CardDescription>
          Records that will be embedded in the next export. Each pack artefact references these by ID so reviewers can
          trace any line back to its source. Click any record to edit it before generating.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2">
          {groups.map((g) =>
            g.items.length === 0 ? null : (
              <div key={g.key}>
                <div className="mb-2 flex items-baseline justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</h3>
                  <span className="text-[10px] text-muted-foreground">{g.items.length}</span>
                </div>
                <ul className="space-y-1">
                  {g.items.slice(0, 5).map((it) => (
                    <li key={it.id}>
                      <Link
                        to={it.editUrl}
                        className="group block rounded-md border border-border bg-muted/30 px-3 py-2 text-xs transition-colors hover:border-primary/40 hover:bg-primary/5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium text-foreground group-hover:text-primary">{it.title}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">{it.id.slice(0, 8)}</span>
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">{it.subtitle}</div>
                      </Link>
                    </li>
                  ))}
                  {g.items.length > 5 && (
                    <li className="px-3 py-1.5 text-[11px] italic text-muted-foreground">
                      +{g.items.length - 5} more (all included in export)
                    </li>
                  )}
                </ul>
              </div>
            ),
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Pill({ ok, muted, children }: { ok: boolean; muted?: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
        ok
          ? 'border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300'
          : muted
          ? 'border-border bg-muted text-muted-foreground'
          : 'border-amber-300/60 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300',
      )}
    >
      {children}
    </span>
  );
}
